import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Square, Eye, ChevronDown, ChevronUp, Zap, MessageCircle, BookOpen, AlertTriangle } from 'lucide-react';

const ACTIVITIES = {
  A1: [
    {
      id: 'accepter-proposition',
      title: 'Accepter une proposition',
      temps: 'Présent',
      situation: {
        a: 'Tu veux un café ?',
        b: 'Oui, je veux bien.',
      },
      comprehension: {
        question: 'Que fait A ?',
        options: [
          { text: 'Il refuse quelque chose', correct: false },
          { text: 'Il propose quelque chose', correct: true },
          { text: "Il demande de l'aide", correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Oui, je veux bien.', quality: 'best' },
          { text: 'Oui, je veux un café maintenant.', quality: 'ok' },
          { text: 'Donne-moi un café.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Oui, je veux bien, merci !',
      modele: 'Oui, je veux bien, merci !',
      variantes: [
        { register: 'Informel', text: 'Oui, avec plaisir !' },
        { register: 'Neutre', text: 'Oui, merci.' },
        { register: 'Plus poli', text: 'Oui, je veux bien, merci beaucoup.' },
      ],
      pourquoi: '"Je veux bien" est une manière naturelle d\'accepter une proposition.',
      aEviter: {
        text: '"Donne-moi un café"',
        explanation: 'Trop direct, peu poli.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ['Tu veux...', 'Je veux...'],
        explanation: 'Situation immédiate.',
      },
    },
    {
      id: 'refuser-poliment',
      title: 'Refuser poliment',
      temps: 'Présent',
      situation: {
        a: 'Tu veux sortir ce soir ?',
        b: 'Non, je suis fatigué.',
      },
      comprehension: {
        question: 'Que fait B ?',
        options: [
          { text: 'Il accepte', correct: false },
          { text: 'Il refuse', correct: true },
          { text: 'Il propose', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Non, désolé, je suis fatigué.', quality: 'best' },
          { text: 'Non.', quality: 'ok' },
          { text: "Je ne viens pas, c'est tout.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Non, désolé, je suis fatigué.',
      modele: 'Non, désolé, je suis fatigué.',
      variantes: [
        { register: 'Informel', text: 'Non, je suis crevé.' },
        { register: 'Neutre', text: 'Non, je ne peux pas.' },
        { register: 'Plus poli', text: 'Non, désolé, je ne peux pas ce soir.' },
      ],
      pourquoi: 'On ajoute une raison pour refuser poliment.',
      aEviter: {
        text: '"Je ne viens pas."',
        explanation: 'Trop direct.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ['Je suis fatigué.', 'Je ne peux pas.'],
        explanation: "Utilisé pour exprimer un état ou une impossibilité au moment présent.",
      },
    },
    {
      id: 'se-presenter',
      title: 'Se présenter',
      temps: 'Présent',
      situation: {
        a: "Bonjour, je m'appelle Marie.",
        b: 'Bonjour !',
      },
      comprehension: {
        question: 'Que fait A ?',
        options: [
          { text: 'Elle demande quelque chose', correct: false },
          { text: 'Elle se présente', correct: true },
          { text: 'Elle refuse', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Bonjour, je m'appelle Priya.", quality: 'best' },
          { text: 'Moi Priya.', quality: 'ok' },
          { text: 'Je suis ici.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Bonjour, je m'appelle Priya.",
      modele: "Bonjour, je m'appelle Priya.",
      variantes: [
        { register: 'Informel', text: "Salut, moi c'est Priya." },
        { register: 'Neutre', text: "Bonjour, je m'appelle Priya." },
        { register: 'Plus poli', text: "Bonjour, je m'appelle Priya, enchantée." },
      ],
      pourquoi: '"Je m\'appelle..." est la structure correcte pour se présenter.',
      aEviter: {
        text: '"Moi Priya"',
        explanation: 'Structure incorrecte.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ["Je m'appelle...", 'Je suis...'],
        explanation: "Utilisé pour se présenter et donner des informations personnelles.",
      },
    },
    {
      id: 'demander-poliment',
      title: 'Demander poliment',
      temps: 'Présent',
      situation: {
        a: 'Excuse-moi, tu as un stylo ?',
        b: 'Oui.',
      },
      comprehension: {
        question: 'Que fait A ?',
        options: [
          { text: 'Il demande quelque chose', correct: true },
          { text: 'Il refuse', correct: false },
          { text: 'Il propose', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Oui, tiens.', quality: 'best' },
          { text: 'Oui.', quality: 'ok' },
          { text: 'Prends-le.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Oui, tiens !',
      modele: 'Oui, tiens !',
      variantes: [
        { register: 'Informel', text: 'Tiens !' },
        { register: 'Neutre', text: 'Oui, voilà.' },
        { register: 'Plus poli', text: 'Oui, bien sûr.' },
      ],
      pourquoi: 'On utilise une réponse courte mais naturelle.',
      aEviter: {
        text: '"Prends-le."',
        explanation: 'Peut sembler brusque.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ['Tu as...', "J'ai..."],
        explanation: "Utilisé pour parler de la possession au moment présent.",
      },
    },
    {
      id: 'accepter-invitation',
      title: 'Accepter une invitation',
      temps: 'Futur proche',
      situation: {
        a: 'Tu veux venir chez moi ce soir ?',
        b: 'Je vais voir...',
      },
      comprehension: {
        question: 'Que veut dire B ?',
        options: [
          { text: 'Il accepte', correct: false },
          { text: 'Il refuse', correct: false },
          { text: 'Il hésite', correct: true },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Je vais essayer de venir.', quality: 'best' },
          { text: 'Oui, je viens.', quality: 'ok' },
          { text: 'Je viens maintenant.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Je vais essayer de venir.',
      modele: 'Je vais essayer de venir.',
      variantes: [
        { register: 'Informel', text: 'Je vais voir !' },
        { register: 'Neutre', text: 'Je vais essayer de venir.' },
        { register: 'Plus poli', text: 'Je vais essayer de passer.' },
      ],
      pourquoi: '"Je vais..." = futur proche — intention ou plan.',
      aEviter: {
        text: '"Je viens maintenant."',
        explanation: 'Pas logique dans ce contexte.',
      },
      pointDeLangue: {
        temps: 'Futur proche',
        examples: ['Je vais venir', 'Je vais essayer'],
        explanation: "Utilisé pour exprimer une intention ou un plan proche.",
      },
    },
    {
      id: 'donner-avis',
      title: 'Donner son avis',
      temps: 'Présent',
      situation: {
        a: 'Tu aimes ce film ?',
        b: 'Oui, il est intéressant.',
      },
      comprehension: {
        question: 'Que fait B ?',
        options: [
          { text: 'Il refuse', correct: false },
          { text: 'Il donne son avis', correct: true },
          { text: 'Il demande', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Oui, j'aime bien.", quality: 'best' },
          { text: 'Film intéressant.', quality: 'ok' },
          { text: 'Je regarde film.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Oui, j'aime bien ce film.",
      modele: "Oui, j'aime bien ce film.",
      variantes: [
        { register: 'Informel', text: "Oui, c'est sympa !" },
        { register: 'Neutre', text: "Oui, j'aime bien." },
        { register: 'Plus poli', text: 'Oui, je le trouve intéressant.' },
      ],
      pourquoi: '"J\'aime bien" est naturel pour exprimer une opinion simple.',
      aEviter: {
        text: '"Film intéressant"',
        explanation: 'Phrase incomplète.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ["J'aime", 'Je trouve'],
        explanation: "Utilisé pour exprimer une opinion ou un goût.",
      },
    },
    {
      id: 'exprimer-preference',
      title: 'Exprimer une préférence',
      temps: 'Présent',
      situation: {
        a: 'Tu préfères le café ou le thé ?',
        b: 'Le thé.',
      },
      comprehension: {
        question: 'Que fait A ?',
        options: [
          { text: 'Il propose', correct: false },
          { text: 'Il demande une préférence', correct: true },
          { text: 'Il refuse', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Je préfère le thé.', quality: 'best' },
          { text: 'Thé.', quality: 'ok' },
          { text: 'Je prends café.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Je préfère le thé.',
      modele: 'Je préfère le thé.',
      variantes: [
        { register: 'Informel', text: 'Le thé, moi.' },
        { register: 'Neutre', text: 'Je préfère le thé.' },
        { register: 'Plus poli', text: 'Je préfère le thé, merci.' },
      ],
      pourquoi: 'Phrase complète = plus naturelle.',
      aEviter: {
        text: '"Thé"',
        explanation: 'Trop court.',
      },
      pointDeLangue: {
        temps: 'Présent',
        examples: ['Je préfère', 'Tu préfères'],
        explanation: "Utilisé pour exprimer un choix ou une préférence.",
      },
    },
    {
      id: 'proposer-activite',
      title: 'Proposer une activité',
      temps: 'Futur proche',
      situation: {
        a: 'On va au restaurant ce soir ?',
        b: 'Bonne idée !',
      },
      comprehension: {
        question: 'Que fait A ?',
        options: [
          { text: 'Il refuse', correct: false },
          { text: 'Il propose', correct: true },
          { text: 'Il demande un objet', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Oui, on va y aller !', quality: 'best' },
          { text: 'Oui, restaurant.', quality: 'ok' },
          { text: 'Je mange seul.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Oui, on va y aller !',
      modele: 'Oui, on va y aller !',
      variantes: [
        { register: 'Informel', text: 'Super, on y va !' },
        { register: 'Neutre', text: 'Oui, bonne idée.' },
        { register: 'Plus poli', text: 'Oui, pourquoi pas.' },
      ],
      pourquoi: '"On va..." = action future proche.',
      aEviter: {
        text: '"Oui, restaurant"',
        explanation: 'Manque de structure.',
      },
      pointDeLangue: {
        temps: 'Futur proche',
        examples: ['On va aller', 'On va manger'],
        explanation: "Utilisé pour proposer ou accepter une activité à venir.",
      },
    },
    {
      id: 'refuser-avec-explication',
      title: 'Refuser avec explication',
      temps: 'Futur proche',
      situation: {
        a: 'Tu viens à la réunion demain ?',
        b: 'Non, je travaille.',
      },
      comprehension: {
        question: 'Pourquoi B refuse ?',
        options: [
          { text: "Il n'aime pas", correct: false },
          { text: 'Il est occupé', correct: true },
          { text: 'Il oublie', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Non, je vais travailler demain.', quality: 'best' },
          { text: 'Non.', quality: 'ok' },
          { text: 'Je ne viens pas.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Non, je vais travailler demain.',
      modele: 'Non, je vais travailler demain.',
      variantes: [
        { register: 'Informel', text: 'Non, je bosse demain.' },
        { register: 'Neutre', text: 'Non, je travaille demain.' },
        { register: 'Plus poli', text: 'Non, désolé, je vais travailler demain.' },
      ],
      pourquoi: 'Ajouter une explication rend le refus plus naturel.',
      aEviter: {
        text: '"Je ne viens pas."',
        explanation: 'Trop direct.',
      },
      pointDeLangue: {
        temps: 'Futur proche',
        examples: ['Je vais travailler', 'Je vais venir'],
        explanation: "Utilisé pour exprimer un plan ou une raison future.",
      },
    },
  ],
  A2: [
    {
      id: 'plan-futur-proche',
      title: "Parler d'un plan",
      temps: 'Futur proche',
      situation: {
        a: "Qu'est-ce que tu vas faire ce soir ?",
        b: 'Je vais regarder un film.',
      },
      comprehension: {
        question: 'De quel temps parle B ?',
        options: [
          { text: 'Présent', correct: false },
          { text: 'Futur proche', correct: true },
          { text: 'Passé', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Je vais rester à la maison et regarder un film.', quality: 'best' },
          { text: 'Je regarde film.', quality: 'ok' },
          { text: "J'ai regardé un film.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Je vais rester à la maison et regarder un film.',
      modele: 'Je vais rester à la maison et regarder un film.',
      variantes: [
        { register: 'Option 1', text: 'Je vais sortir avec des amis.' },
        { register: 'Option 2', text: 'Je vais étudier ce soir.' },
        { register: 'Option 3', text: 'Je vais me reposer.' },
      ],
      pourquoi: 'Le futur proche (aller + infinitif) est utilisé pour parler de projets immédiats ou proches dans le temps.',
      aEviter: {
        text: '"J\'ai regardé un film"',
        explanation: "C'est du passé composé — mauvais temps pour parler d'un plan futur.",
      },
      pointDeLangue: {
        temps: 'Futur proche',
        examples: ['Je vais + infinitif'],
        explanation: "Utilisé pour parler d'une action prévue dans un futur proche.",
      },
    },
    {
      id: 'action-passee',
      title: 'Raconter une action passée',
      temps: 'Passé composé',
      situation: {
        a: "Qu'est-ce que tu as fait hier ?",
        b: "J'ai travaillé toute la journée.",
      },
      comprehension: {
        question: 'De quel moment parle B ?',
        options: [
          { text: 'Le futur', correct: false },
          { text: 'Le présent', correct: false },
          { text: 'Le passé', correct: true },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "J'ai étudié et j'ai fait du sport.", quality: 'best' },
          { text: 'Je fais du sport hier.', quality: 'ok' },
          { text: 'Je vais étudier.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "J'ai étudié et j'ai fait du sport.",
      modele: "J'ai étudié et j'ai fait du sport.",
      variantes: [
        { register: 'Option 1', text: "J'ai regardé un film." },
        { register: 'Option 2', text: "J'ai vu mes amis." },
        { register: 'Option 3', text: "J'ai travaillé." },
      ],
      pourquoi: "Le passé composé est utilisé pour raconter des actions terminées dans le passé.",
      aEviter: {
        text: '"Je vais étudier"',
        explanation: "C'est du futur proche — mauvais temps pour raconter le passé.",
      },
      pointDeLangue: {
        temps: 'Passé composé',
        examples: ["J'ai + participe passé"],
        explanation: "Utilisé pour parler d'actions terminées dans le passé.",
      },
    },
    {
      id: 'situation-passee-imparfait',
      title: 'Décrire une situation passée',
      temps: 'Imparfait',
      situation: {
        a: "Quand tu étais enfant, tu aimais l'école ?",
        b: "Oui, j'aimais beaucoup.",
      },
      comprehension: {
        question: 'De quel temps parle B ?',
        options: [
          { text: 'Présent', correct: false },
          { text: 'Imparfait', correct: true },
          { text: 'Futur', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Oui, j'aimais apprendre et jouer avec mes amis.", quality: 'best' },
          { text: "J'aime école.", quality: 'ok' },
          { text: "J'ai aimé école.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Oui, j'aimais apprendre et jouer avec mes amis.",
      modele: "Oui, j'aimais apprendre et jouer avec mes amis.",
      variantes: [
        { register: 'Option 1', text: "Non, je n'aimais pas ça." },
        { register: 'Option 2', text: "Oui, j'aimais mes professeurs." },
        { register: 'Option 3', text: "Non, c'était difficile." },
      ],
      pourquoi: "L'imparfait est utilisé pour décrire des habitudes ou des situations dans le passé.",
      aEviter: {
        text: '"J\'ai aimé école"',
        explanation: "Le passé composé exprime une action ponctuelle, pas une habitude. Et il manque l'article.",
      },
      pointDeLangue: {
        temps: 'Imparfait',
        examples: ["J'aimais", "C'était"],
        explanation: "Utilisé pour décrire des habitudes, des états ou des descriptions dans le passé.",
      },
    },
    {
      id: 'futur-simple',
      title: 'Parler du futur',
      temps: 'Futur simple',
      situation: {
        a: 'Que feras-tu après tes études ?',
        b: 'Je travaillerai dans une entreprise.',
      },
      comprehension: {
        question: 'De quel temps parle B ?',
        options: [
          { text: 'Passé', correct: false },
          { text: 'Futur simple', correct: true },
          { text: 'Présent', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Je voyagerai et je travaillerai à l'étranger.", quality: 'best' },
          { text: 'Je vais voyager.', quality: 'ok' },
          { text: 'Je voyage hier.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Je voyagerai et je travaillerai à l'étranger.",
      modele: "Je voyagerai et je travaillerai à l'étranger.",
      variantes: [
        { register: 'Option 1', text: 'Je continuerai mes études.' },
        { register: 'Option 2', text: 'Je travaillerai en France.' },
        { register: 'Option 3', text: 'Je créerai une entreprise.' },
      ],
      pourquoi: "Le futur simple est utilisé pour parler de projets ou de prédictions à long terme.",
      aEviter: {
        text: '"Je voyage hier"',
        explanation: 'Mélange de présent et de marqueur temporel du passé — incohérent.',
      },
      pointDeLangue: {
        temps: 'Futur simple',
        examples: ['Je + verbe + ai (travaillerai)'],
        explanation: "Utilisé pour exprimer des actions futures, des projets ou des prédictions.",
      },
    },
    {
      id: 'combiner-passe-present',
      title: 'Combiner passé + présent',
      temps: 'Passé composé + Présent',
      situation: {
        a: "Tu es fatigué aujourd'hui ?",
        b: "Oui, j'ai mal dormi.",
      },
      comprehension: {
        question: 'Pourquoi B est fatigué ?',
        options: [
          { text: 'Il travaille', correct: false },
          { text: 'Il a mal dormi', correct: true },
          { text: 'Il sort', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Oui, j'ai mal dormi et je suis fatigué aujourd'hui.", quality: 'best' },
          { text: 'Je suis fatigué.', quality: 'ok' },
          { text: 'Je vais dormir.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Oui, j'ai mal dormi et je suis fatigué aujourd'hui.",
      modele: "Oui, j'ai mal dormi et je suis fatigué aujourd'hui.",
      variantes: [
        { register: 'Option 1', text: "Oui, j'ai peu dormi." },
        { register: 'Option 2', text: "Oui, je suis très fatigué aujourd'hui." },
        { register: 'Option 3', text: "Oui, j'ai travaillé tard." },
      ],
      pourquoi: "On combine le passé composé (cause passée) et le présent (état actuel) pour expliquer une situation.",
      aEviter: {
        text: '"Je vais dormir"',
        explanation: "C'est du futur proche — ne répond pas à la question sur l'état actuel.",
      },
      pointDeLangue: {
        temps: 'Passé composé + Présent',
        examples: ["J'ai mal dormi", 'Je suis fatigué'],
        explanation: "Combinaison du passé composé (action passée) et du présent (état actuel).",
      },
    },
    {
      id: 'donner-raison',
      title: 'Donner une raison',
      temps: 'Présent + parce que',
      situation: {
        a: 'Tu veux sortir ce soir ?',
        b: 'Non, je travaille.',
      },
      comprehension: {
        question: 'Pourquoi B refuse ?',
        options: [
          { text: "Il n'aime pas sortir", correct: false },
          { text: 'Il travaille', correct: true },
          { text: 'Il est malade', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: 'Non, je ne peux pas sortir parce que je travaille ce soir.', quality: 'best' },
          { text: 'Non, je travaille.', quality: 'ok' },
          { text: 'Non, sortir.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: 'Non, je ne peux pas sortir parce que je travaille ce soir.',
      modele: 'Non, je ne peux pas sortir parce que je travaille ce soir.',
      variantes: [
        { register: 'Option 1', text: 'Non, je travaille, donc je reste chez moi.' },
        { register: 'Option 2', text: 'Non, désolé, je dois travailler.' },
        { register: 'Option 3', text: "Non, je ne peux pas, j'ai du travail." },
      ],
      pourquoi: '"Parce que" permet d\'exprimer une cause et de justifier un refus de manière claire et naturelle.',
      aEviter: {
        text: '"Non, sortir."',
        explanation: 'Phrase incomplète et incompréhensible.',
      },
      pointDeLangue: {
        temps: 'Parce que (exprimer une cause)',
        examples: ['Je ne peux pas... parce que...'],
        explanation: "Utilisé pour donner une raison ou une explication.",
      },
    },
    {
      id: 'opinion-justification',
      title: 'Donner une opinion avec justification',
      temps: 'Présent + parce que',
      situation: {
        a: 'Tu aimes vivre en ville ?',
        b: "Oui, c'est pratique.",
      },
      comprehension: {
        question: 'Pourquoi B aime la ville ?',
        options: [
          { text: "Parce que c'est pratique", correct: true },
          { text: "Parce que c'est calme", correct: false },
          { text: "Parce que c'est petit", correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Oui, j'aime vivre en ville parce que c'est pratique et intéressant.", quality: 'best' },
          { text: 'Oui, ville bien.', quality: 'ok' },
          { text: 'Ville non.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Oui, j'aime vivre en ville parce que c'est pratique et intéressant.",
      modele: "Oui, j'aime vivre en ville parce que c'est pratique et intéressant.",
      variantes: [
        { register: 'Option 1', text: "Oui, parce qu'il y a beaucoup de choses à faire." },
        { register: 'Option 2', text: "Oui, c'est pratique pour le travail." },
        { register: 'Option 3', text: "Non, je préfère la campagne parce que c'est calme." },
      ],
      pourquoi: 'Donner une opinion avec "parce que" + 2 idées montre une capacité à argumenter simplement.',
      aEviter: {
        text: '"Ville non."',
        explanation: 'Phrase incomplète — pas de verbe, pas de structure.',
      },
      pointDeLangue: {
        temps: 'Parce que + 2 idées',
        examples: ["J'aime... parce que c'est... et..."],
        explanation: "Utilisé pour donner une opinion justifiée avec deux arguments.",
      },
    },
    {
      id: 'passe-justification',
      title: 'Expliquer une absence passée',
      temps: 'Passé composé + parce que',
      situation: {
        a: "Pourquoi tu n'es pas venu hier ?",
        b: "J'ai été malade.",
      },
      comprehension: {
        question: "Pourquoi B n'est pas venu ?",
        options: [
          { text: 'Il a oublié', correct: false },
          { text: 'Il était malade', correct: true },
          { text: 'Il travaillait', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Je ne suis pas venu parce que j'ai été malade.", quality: 'best' },
          { text: 'Pas venu, malade.', quality: 'ok' },
          { text: 'Je viens.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Je ne suis pas venu parce que j'ai été malade.",
      modele: "Je ne suis pas venu parce que j'ai été malade.",
      variantes: [
        { register: 'Option 1', text: "Je n'ai pas pu venir parce que j'étais malade." },
        { register: 'Option 2', text: "J'étais malade, donc je suis resté chez moi." },
        { register: 'Option 3', text: "J'ai été malade toute la journée." },
      ],
      pourquoi: "Le passé composé + parce que permet d'expliquer une action passée avec sa cause.",
      aEviter: {
        text: '"Je viens."',
        explanation: "C'est du présent — ne répond pas à une question sur le passé.",
      },
      pointDeLangue: {
        temps: 'Passé composé + parce que',
        examples: ['Je ne suis pas venu parce que...'],
        explanation: "Utilisé pour justifier une action ou une absence dans le passé.",
      },
    },
    {
      id: 'futur-intention',
      title: 'Exprimer une intention future',
      temps: 'Futur proche + raison',
      situation: {
        a: 'Tu vas étudier ce week-end ?',
        b: 'Oui, un peu.',
      },
      comprehension: {
        question: 'Que va faire B ?',
        options: [
          { text: 'Il a étudié', correct: false },
          { text: 'Il va étudier', correct: true },
          { text: 'Il étudie maintenant', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Oui, je vais étudier parce que j'ai un examen.", quality: 'best' },
          { text: 'Oui, étudier.', quality: 'ok' },
          { text: "J'ai étudié.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Oui, je vais étudier parce que j'ai un examen.",
      modele: "Oui, je vais étudier parce que j'ai un examen.",
      variantes: [
        { register: 'Option 1', text: 'Oui, je vais réviser mes cours.' },
        { register: 'Option 2', text: 'Oui, je dois étudier.' },
        { register: 'Option 3', text: 'Oui, je vais travailler un peu.' },
      ],
      pourquoi: "Le futur proche + parce que permet d'exprimer une intention et sa motivation.",
      aEviter: {
        text: '"J\'ai étudié."',
        explanation: "C'est du passé — ne correspond pas à une intention future.",
      },
      pointDeLangue: {
        temps: 'Futur proche + raison',
        examples: ["Je vais étudier parce que j'ai..."],
        explanation: "Utilisé pour parler d'un plan futur avec sa justification.",
      },
    },
    {
      id: 'comparer-expliquer',
      title: 'Comparer et expliquer',
      temps: 'Présent + comparaison + parce que',
      situation: {
        a: 'Tu préfères travailler seul ou en groupe ?',
        b: 'En groupe.',
      },
      comprehension: {
        question: 'Que préfère B ?',
        options: [
          { text: 'Travailler seul', correct: false },
          { text: 'Travailler en groupe', correct: true },
          { text: 'Ne pas travailler', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Imagine que tu es B.',
        options: [
          { text: "Je préfère travailler en groupe parce que c'est plus motivant.", quality: 'best' },
          { text: 'Groupe mieux.', quality: 'ok' },
          { text: 'Je travaille seul.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse à voix haute (10-15 secondes)',
      recordExample: "Je préfère travailler en groupe parce que c'est plus motivant.",
      modele: "Je préfère travailler en groupe parce que c'est plus motivant.",
      variantes: [
        { register: 'Option 1', text: "Parce que c'est plus facile." },
        { register: 'Option 2', text: "Parce que j'aime travailler avec les autres." },
        { register: 'Option 3', text: "Parce que c'est plus intéressant." },
      ],
      pourquoi: 'La comparaison + parce que permet de justifier un choix de manière argumentée.',
      aEviter: {
        text: '"Groupe mieux."',
        explanation: 'Phrase incomplète — manque de verbe et de structure.',
      },
      pointDeLangue: {
        temps: 'Comparaison + parce que',
        examples: ["Je préfère... parce que c'est plus..."],
        explanation: "Utilisé pour exprimer une préférence avec une comparaison justifiée.",
      },
    },
  ],
  B1: [
    {
      id: 'exprimer-doute',
      title: 'Exprimer un doute',
      temps: 'Doute + nuance',
      situation: {
        a: "Tu penses qu'on va réussir ce projet ?",
        b: 'Je ne suis pas sûr.',
      },
      comprehension: {
        question: 'Que pense B ?',
        options: [
          { text: 'Il est certain', correct: false },
          { text: 'Il doute', correct: true },
          { text: 'Il refuse', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 2-3 phrases.',
        options: [
          { text: "Je ne suis pas sûr que ce soit facile. On a encore beaucoup de travail à faire, et le temps est limité. Mais si on s'organise bien, on peut réussir.", quality: 'best' },
          { text: 'Je ne suis pas sûr.', quality: 'ok' },
          { text: 'Non.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse en 2-3 phrases',
      recordExample: "Je ne suis pas sûr que ce soit facile. Mais si on s'organise bien, on peut réussir.",
      modele: "Je ne suis pas sûr que ce soit facile. On a encore beaucoup de travail à faire, et le temps est limité. Mais si on s'organise bien, on peut réussir.",
      variantes: [
        { register: 'Option 1', text: "Je doute un peu, mais tout dépend de notre organisation." },
        { register: 'Option 2', text: "Ce n'est pas certain, il faudrait mieux planifier." },
        { register: 'Option 3', text: "Je ne sais pas, mais je pense qu'on peut essayer." },
      ],
      pourquoi: 'Exprimer un doute avec nuance montre une réflexion et une capacité à argumenter de manière équilibrée.',
      aEviter: {
        text: '"Non."',
        explanation: 'Trop direct et sans nuance — ne développe pas la pensée.',
      },
      pointDeLangue: {
        temps: 'Exprimer le doute + nuance',
        examples: ['Je ne suis pas sûr que...', 'Mais si on...'],
        explanation: "Combinaison du doute (subjonctif) et d'une nuance positive pour équilibrer le propos.",
      },
    },
    {
      id: 'convaincre',
      title: 'Convaincre quelqu\'un',
      temps: 'Convaincre + arguments',
      situation: {
        a: 'Je ne veux pas aller au cinéma.',
        b: 'Pourquoi ?',
      },
      comprehension: {
        question: 'Que veut faire B ?',
        options: [
          { text: 'Refuser', correct: false },
          { text: 'Comprendre et convaincre', correct: true },
          { text: 'Partir', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 2-3 phrases pour convaincre.',
        options: [
          { text: "Tu devrais venir, ça va te faire du bien. Le film est très intéressant, et on peut passer un bon moment ensemble. En plus, ça change un peu de la routine.", quality: 'best' },
          { text: 'Viens.', quality: 'ok' },
          { text: 'Tu dois venir.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse en 2-3 phrases',
      recordExample: "Tu devrais venir, ça va te faire du bien. En plus, ça change un peu de la routine.",
      modele: "Tu devrais venir, ça va te faire du bien. Le film est très intéressant, et on peut passer un bon moment ensemble. En plus, ça change un peu de la routine.",
      variantes: [
        { register: 'Option 1', text: "Allez, viens ! On va bien s'amuser, et le film a de très bonnes critiques." },
        { register: 'Option 2', text: "Je pense que ça te plairait. C'est une bonne occasion de sortir un peu." },
        { register: 'Option 3', text: "Essaie au moins, tu ne vas pas le regretter. Et après, on peut aller manger quelque chose." },
      ],
      pourquoi: 'Convaincre demande des arguments variés et un ton encourageant, pas un ordre direct.',
      aEviter: {
        text: '"Tu dois venir."',
        explanation: "Trop autoritaire — on ne convainc pas avec un ordre.",
      },
      pointDeLangue: {
        temps: 'Convaincre + arguments',
        examples: ['Tu devrais...', 'En plus...', 'On peut...'],
        explanation: "Utiliser le conditionnel (tu devrais) et des connecteurs (en plus, et) pour argumenter.",
      },
    },
    {
      id: 'reagir-probleme',
      title: 'Réagir à un problème',
      temps: 'Réagir + conseiller',
      situation: {
        a: "J'ai raté mon train ce matin.",
        b: 'Ah bon ?',
      },
      comprehension: {
        question: "Que s'est-il passé ?",
        options: [
          { text: 'A est en avance', correct: false },
          { text: 'A a raté le train', correct: true },
          { text: 'A part en voyage', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 2-3 phrases.',
        options: [
          { text: "Ah, c'est vraiment dommage. Tu devrais vérifier les horaires la prochaine fois. Mais ne t'inquiète pas, ça arrive à tout le monde.", quality: 'best' },
          { text: 'Dommage.', quality: 'ok' },
          { text: "Ce n'est pas grave.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse en 2-3 phrases',
      recordExample: "Ah, c'est vraiment dommage. Ne t'inquiète pas, ça arrive à tout le monde.",
      modele: "Ah, c'est vraiment dommage. Tu devrais vérifier les horaires la prochaine fois. Mais ne t'inquiète pas, ça arrive à tout le monde.",
      variantes: [
        { register: 'Option 1', text: "Oh non ! Tu as pu prendre le suivant ? La prochaine fois, mets une alarme." },
        { register: 'Option 2', text: "C'est ennuyeux. Tu devrais partir plus tôt la prochaine fois. Courage !" },
        { register: 'Option 3', text: "Quel dommage ! Ça m'est arrivé aussi. Il faut toujours prévoir un peu de marge." },
      ],
      pourquoi: "Réagir à un problème demande de l'empathie + un conseil constructif, pas juste une minimisation.",
      aEviter: {
        text: '"Ce n\'est pas grave."',
        explanation: "Minimise le problème sans montrer d'empathie ni donner de conseil.",
      },
      pointDeLangue: {
        temps: 'Réagir + conseiller',
        examples: ['Tu devrais...', "Ne t'inquiète pas...", 'Ça arrive à...'],
        explanation: "Combiner une réaction empathique avec un conseil au conditionnel.",
      },
    },
    {
      id: 'faire-suggestion',
      title: 'Faire une suggestion',
      temps: 'Suggestion (on pourrait...)',
      situation: {
        a: 'On fait quoi ce week-end ?',
        b: 'Je ne sais pas.',
      },
      comprehension: {
        question: 'Que veut faire A ?',
        options: [
          { text: 'Refuser', correct: false },
          { text: 'Proposer une idée', correct: true },
          { text: 'Partir', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 2-3 phrases.',
        options: [
          { text: "On pourrait aller au parc ou visiter un musée. Il va faire beau, donc c'est une bonne idée de sortir. Qu'est-ce que tu en penses ?", quality: 'best' },
          { text: 'Parc.', quality: 'ok' },
          { text: 'Je ne sais pas.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse en 2-3 phrases',
      recordExample: "On pourrait aller au parc. Qu'est-ce que tu en penses ?",
      modele: "On pourrait aller au parc ou visiter un musée. Il va faire beau, donc c'est une bonne idée de sortir. Qu'est-ce que tu en penses ?",
      variantes: [
        { register: 'Option 1', text: "Pourquoi pas un pique-nique ? Il va faire beau et ce serait agréable." },
        { register: 'Option 2', text: "On pourrait essayer ce nouveau restaurant. J'ai entendu dire qu'il est très bon." },
        { register: 'Option 3', text: "Si tu veux, on peut faire une randonnée. Ça nous ferait du bien de prendre l'air." },
      ],
      pourquoi: '"On pourrait" est une manière polie et ouverte de faire une suggestion sans imposer.',
      aEviter: {
        text: '"Je ne sais pas."',
        explanation: "Ne propose rien — ne fait pas avancer la conversation.",
      },
      pointDeLangue: {
        temps: 'Suggestion (on pourrait...)',
        examples: ['On pourrait...', "Qu'est-ce que tu en penses ?"],
        explanation: "Le conditionnel (on pourrait) pour suggérer + une question pour impliquer l'autre.",
      },
    },
    {
      id: 'preference-contraste',
      title: 'Exprimer une préférence avec contraste',
      temps: 'Préférence + contraste',
      situation: {
        a: 'Tu préfères travailler le matin ou le soir ?',
        b: 'Le matin.',
      },
      comprehension: {
        question: 'Que préfère B ?',
        options: [
          { text: 'Le soir', correct: false },
          { text: 'Le matin', correct: true },
          { text: 'Aucun', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 2-3 phrases.',
        options: [
          { text: "Je préfère travailler le matin parce que je suis plus concentré. Le soir, je suis souvent fatigué. Cependant, ça dépend aussi du travail à faire.", quality: 'best' },
          { text: 'Matin.', quality: 'ok' },
          { text: 'Je travaille soir.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Dis ta réponse en 2-3 phrases',
      recordExample: "Je préfère travailler le matin parce que je suis plus concentré. Cependant, ça dépend du travail.",
      modele: "Je préfère travailler le matin parce que je suis plus concentré. Le soir, je suis souvent fatigué. Cependant, ça dépend aussi du travail à faire.",
      variantes: [
        { register: 'Option 1', text: "Le matin, je suis plus productif. Par contre, le soir je préfère me détendre." },
        { register: 'Option 2', text: "Je travaille mieux le matin. Mais parfois, le soir c'est plus calme pour réfléchir." },
        { register: 'Option 3', text: "Le matin sans hésitation. Néanmoins, certains projets demandent du calme, et le soir c'est plus tranquille." },
      ],
      pourquoi: 'Exprimer une préférence avec contraste (cependant, par contre) montre une pensée nuancée.',
      aEviter: {
        text: '"Je travaille soir."',
        explanation: "Ne correspond pas à la préférence exprimée et la phrase est incorrecte.",
      },
      pointDeLangue: {
        temps: 'Préférence + contraste',
        examples: ['Je préfère... parce que...', 'Cependant...', 'Par contre...'],
        explanation: "Exprimer un choix justifié + introduire une nuance avec un connecteur d'opposition.",
      },
    },
  ],
  B2: [
    {
      id: 'refus-diplomatique',
      title: 'Refus indirect et diplomatique',
      temps: 'Refus indirect + diplomatie',
      situation: {
        a: 'Tu peux finir ce travail aujourd\'hui ?',
        b: 'Je vais faire de mon mieux...',
      },
      comprehension: {
        question: 'Que veut dire B ?',
        options: [
          { text: 'Il accepte complètement', correct: false },
          { text: 'Il refuse indirectement / hésite', correct: true },
          { text: 'Il a déjà fini', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec nuance.',
        options: [
          { text: "Je vais faire de mon mieux, mais je préfère être honnête : cela risque d'être compliqué aujourd'hui. J'ai déjà plusieurs tâches urgentes à terminer. En revanche, je peux m'en occuper en priorité demain matin. Cela me permettra de faire un travail plus soigné.", quality: 'best' },
          { text: 'Je vais essayer.', quality: 'ok' },
          { text: 'Non, je ne fais pas.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Je vais faire de mon mieux, mais cela risque d'être compliqué aujourd'hui. En revanche, je peux m'en occuper demain matin.",
      modele: "Je vais faire de mon mieux, mais je préfère être honnête : cela risque d'être compliqué aujourd'hui. J'ai déjà plusieurs tâches urgentes à terminer. En revanche, je peux m'en occuper en priorité demain matin. Cela me permettra de faire un travail plus soigné.",
      variantes: [
        { register: 'Option 1', text: "Honnêtement, le délai est un peu serré. Je préférerais avoir jusqu'à demain pour garantir la qualité." },
        { register: 'Option 2', text: "Je comprends l'urgence, mais j'ai d'autres priorités. Serait-il possible de reporter à demain ?" },
        { register: 'Option 3', text: "Je vais voir ce que je peux faire, mais je ne peux rien promettre. Demain serait plus réaliste." },
      ],
      pourquoi: "Un refus diplomatique utilise l'atténuation (risque d'être, je préfère) et propose une alternative constructive.",
      aEviter: {
        text: '"Non, je ne fais pas."',
        explanation: 'Trop direct et grammaticalement incorrect — manque de diplomatie.',
      },
      pointDeLangue: {
        temps: 'Refus indirect + diplomatie (atténuation)',
        examples: ['Cela risque de...', 'En revanche, je peux...', 'Cela me permettra de...'],
        explanation: "L'atténuation et la proposition d'alternative montrent une communication professionnelle et nuancée.",
      },
    },
    {
      id: 'desaccord-nuance',
      title: 'Exprimer un désaccord nuancé',
      temps: 'Désaccord diplomatique + connecteurs',
      situation: {
        a: "À mon avis, travailler à l'étranger n'a que des avantages.",
        b: "Je ne suis pas tout à fait d'accord.",
      },
      comprehension: {
        question: 'Quelle est la position de B ?',
        options: [
          { text: "Il est totalement d'accord", correct: false },
          { text: 'Il nuance / désaccord partiel', correct: true },
          { text: 'Il refuse de répondre', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec argumentation.',
        options: [
          { text: "Je comprends ton point de vue, et c'est vrai qu'il y a de nombreux avantages. Cependant, il ne faut pas négliger les difficultés, comme l'adaptation culturelle ou l'éloignement de la famille. D'ailleurs, certaines personnes ont du mal à s'intégrer dans un nouvel environnement. En fin de compte, tout dépend du profil de chacun.", quality: 'best' },
          { text: "Non, ce n'est pas vrai.", quality: 'ok' },
          { text: 'Tu as tort.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Je comprends ton point de vue, mais il ne faut pas négliger les difficultés. En fin de compte, tout dépend du profil de chacun.",
      modele: "Je comprends ton point de vue, et c'est vrai qu'il y a de nombreux avantages. Cependant, il ne faut pas négliger les difficultés, comme l'adaptation culturelle ou l'éloignement de la famille. D'ailleurs, certaines personnes ont du mal à s'intégrer dans un nouvel environnement. En fin de compte, tout dépend du profil de chacun.",
      variantes: [
        { register: 'Option 1', text: "C'est un point intéressant, néanmoins la réalité est plus complexe. L'isolement et le choc culturel sont des défis réels." },
        { register: 'Option 2', text: "Je vois ce que tu veux dire, toutefois il y a aussi des inconvénients majeurs à considérer." },
        { register: 'Option 3', text: "Tu as raison sur certains points, mais il serait réducteur de dire que c'est uniquement positif." },
      ],
      pourquoi: "Un désaccord nuancé reconnaît d'abord le point de vue de l'autre avant d'introduire des contre-arguments.",
      aEviter: {
        text: '"Tu as tort."',
        explanation: "Trop frontal et impoli — n'ouvre pas au dialogue.",
      },
      pointDeLangue: {
        temps: 'Désaccord diplomatique + connecteurs avancés',
        examples: ['Cependant...', "D'ailleurs...", 'En fin de compte...'],
        explanation: "Utiliser des connecteurs d'opposition et de conclusion pour structurer un désaccord respectueux.",
      },
    },
    {
      id: 'avis-argumente',
      title: 'Donner un avis argumenté avec exemple',
      temps: 'Argument + exemple + nuance',
      situation: {
        a: 'Penses-tu que les réseaux sociaux influencent trop les jeunes ?',
        b: 'Oui, absolument.',
      },
      comprehension: {
        question: "Quelle est l'opinion de B ?",
        options: [
          { text: "Il n'est pas sûr", correct: false },
          { text: "Il est d'accord fortement", correct: true },
          { text: 'Il refuse', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec exemple.',
        options: [
          { text: "Oui, je pense qu'ils ont une influence importante, parfois même excessive. Par exemple, beaucoup de jeunes passent plusieurs heures par jour en ligne, ce qui peut nuire à leur concentration. De plus, ils sont souvent exposés à des contenus peu réalistes. Cela dit, utilisés intelligemment, ils peuvent aussi être un outil intéressant. Tout est une question d'équilibre.", quality: 'best' },
          { text: 'Oui, beaucoup.', quality: 'ok' },
          { text: 'Non.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Oui, je pense qu'ils ont une influence excessive. Par exemple, beaucoup de jeunes passent des heures en ligne. Cela dit, tout est une question d'équilibre.",
      modele: "Oui, je pense qu'ils ont une influence importante, parfois même excessive. Par exemple, beaucoup de jeunes passent plusieurs heures par jour en ligne, ce qui peut nuire à leur concentration. De plus, ils sont souvent exposés à des contenus peu réalistes. Cela dit, utilisés intelligemment, ils peuvent aussi être un outil intéressant. Tout est une question d'équilibre.",
      variantes: [
        { register: 'Option 1', text: "Sans aucun doute. Les réseaux sociaux créent une pression constante sur l'image de soi, notamment chez les adolescents." },
        { register: 'Option 2', text: "Oui, leur influence est indéniable. Toutefois, il faut aussi reconnaître leur rôle dans la diffusion de l'information." },
        { register: 'Option 3', text: "Absolument. Le temps passé sur les écrans est alarmant. Néanmoins, interdire n'est pas la solution — il faut éduquer." },
      ],
      pourquoi: "Un avis argumenté B2 nécessite un exemple concret, des connecteurs variés et une conclusion nuancée.",
      aEviter: {
        text: '"Non."',
        explanation: "Pas d'argument, pas de nuance — ne correspond pas au niveau B2.",
      },
      pointDeLangue: {
        temps: 'Argument + exemple + nuance',
        examples: ['Par exemple...', 'De plus...', 'Cela dit...'],
        explanation: "Structurer une opinion avec un argument, un exemple illustratif et une nuance finale.",
      },
    },
    {
      id: 'proposer-solution',
      title: 'Proposer une solution à un problème',
      temps: 'Proposition + structuration',
      situation: {
        a: 'Notre équipe a du mal à respecter les délais.',
        b: "Oui, c'est vrai.",
      },
      comprehension: {
        question: 'Quel est le problème ?',
        options: [
          { text: "L'équipe travaille trop vite", correct: false },
          { text: "L'équipe ne respecte pas les délais", correct: true },
          { text: "L'équipe refuse de travailler", correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec solution.',
        options: [
          { text: "À mon avis, il faudrait mieux organiser le travail et définir des priorités claires. On pourrait aussi répartir les tâches de manière plus équilibrée entre les membres de l'équipe. Par ailleurs, mettre en place un suivi régulier permettrait d'éviter les retards. Enfin, une meilleure communication serait essentielle pour anticiper les problèmes.", quality: 'best' },
          { text: 'Il faut travailler.', quality: 'ok' },
          { text: "Ce n'est pas possible.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Il faudrait mieux organiser le travail. On pourrait répartir les tâches plus équitablement. Une meilleure communication serait essentielle.",
      modele: "À mon avis, il faudrait mieux organiser le travail et définir des priorités claires. On pourrait aussi répartir les tâches de manière plus équilibrée entre les membres de l'équipe. Par ailleurs, mettre en place un suivi régulier permettrait d'éviter les retards. Enfin, une meilleure communication serait essentielle pour anticiper les problèmes.",
      variantes: [
        { register: 'Option 1', text: "Je suggérerais de commencer par un audit des processus actuels. Ensuite, on pourrait identifier les goulots d'étranglement." },
        { register: 'Option 2', text: "Il serait judicieux d'utiliser un outil de gestion de projet. Cela permettrait à chacun de visualiser les échéances." },
        { register: 'Option 3', text: "On devrait organiser des points hebdomadaires pour suivre l'avancement et anticiper les blocages." },
      ],
      pourquoi: "Proposer des solutions structurées avec le conditionnel (il faudrait, on pourrait) montre une pensée analytique et professionnelle.",
      aEviter: {
        text: '"Ce n\'est pas possible."',
        explanation: "Attitude défaitiste qui ne propose aucune solution constructive.",
      },
      pointDeLangue: {
        temps: 'Proposition + structuration (il faudrait, on pourrait...)',
        examples: ['Il faudrait...', 'On pourrait aussi...', 'Par ailleurs...', 'Enfin...'],
        explanation: "Le conditionnel pour proposer + des connecteurs de structuration (par ailleurs, enfin) pour organiser les idées.",
      },
    },
    {
      id: 'opinion-nuancee',
      title: 'Exprimer une opinion personnelle nuancée',
      temps: 'Nuance + structuration + conclusion',
      situation: {
        a: 'Est-ce que voyager est toujours bénéfique ?',
        b: 'Pas forcément.',
      },
      comprehension: {
        question: 'Que pense B ?',
        options: [
          { text: 'Toujours positif', correct: false },
          { text: 'Toujours négatif', correct: false },
          { text: 'Cela dépend / nuance', correct: true },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec nuance.',
        options: [
          { text: "Je dirais que voyager est généralement enrichissant, car cela permet de découvrir d'autres cultures. Cependant, tout dépend des conditions du voyage. Par exemple, un séjour mal organisé peut être stressant. De plus, certains voyages peuvent avoir un impact environnemental important. En somme, c'est une expérience positive, mais pas dans tous les cas.", quality: 'best' },
          { text: "Oui, c'est bien.", quality: 'ok' },
          { text: 'Non.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Je dirais que voyager est généralement enrichissant. Cependant, tout dépend des conditions. En somme, c'est positif, mais pas toujours.",
      modele: "Je dirais que voyager est généralement enrichissant, car cela permet de découvrir d'autres cultures. Cependant, tout dépend des conditions du voyage. Par exemple, un séjour mal organisé peut être stressant. De plus, certains voyages peuvent avoir un impact environnemental important. En somme, c'est une expérience positive, mais pas dans tous les cas.",
      variantes: [
        { register: 'Option 1', text: "En règle générale, oui. Mais il faut nuancer : le tourisme de masse pose des problèmes éthiques et écologiques." },
        { register: 'Option 2', text: "Cela dépend. Voyager pour s'ouvrir au monde, oui. Voyager pour fuir ses problèmes, non." },
        { register: 'Option 3', text: "Je pense que l'essentiel est la manière dont on voyage. Un voyage réfléchi est toujours bénéfique, contrairement au tourisme superficiel." },
      ],
      pourquoi: "Une opinion nuancée B2 utilise des atténuateurs (généralement, pas forcément), des exemples et une conclusion équilibrée.",
      aEviter: {
        text: '"Non."',
        explanation: "Réponse binaire sans argumentation — ne reflète pas le niveau B2.",
      },
      pointDeLangue: {
        temps: 'Nuance + structuration + conclusion',
        examples: ['Je dirais que...', 'Cependant...', 'En somme...'],
        explanation: "Structurer une opinion avec une thèse atténuée, des arguments contrastés et une conclusion synthétique.",
      },
    },
    {
      id: 'ironie',
      title: "Comprendre et utiliser l'ironie",
      temps: 'Ironie + autocritique + réparation',
      situation: {
        a: 'Tu as encore oublié le rendez-vous...',
        b: 'Oui, bravo à moi...',
      },
      comprehension: {
        question: 'Que veut dire B ?',
        options: [
          { text: 'Il est fier', correct: false },
          { text: 'Il est ironique / critique lui-même', correct: true },
          { text: 'Il est content', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec nuance.',
        options: [
          { text: "Oui, je sais, ce n'est vraiment pas sérieux de ma part. J'aurais dû mieux m'organiser pour ne pas oublier. Cela dit, j'ai eu une journée très chargée. Je vais faire en sorte que cela ne se reproduise pas.", quality: 'best' },
          { text: 'Oui, bravo.', quality: 'ok' },
          { text: "Ce n'est pas grave.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Oui, je sais, ce n'est pas sérieux de ma part. J'aurais dû mieux m'organiser. Je vais faire en sorte que cela ne se reproduise pas.",
      modele: "Oui, je sais, ce n'est vraiment pas sérieux de ma part. J'aurais dû mieux m'organiser pour ne pas oublier. Cela dit, j'ai eu une journée très chargée. Je vais faire en sorte que cela ne se reproduise pas.",
      variantes: [
        { register: 'Option 1', text: "Je reconnais que c'est une mauvaise habitude. Je vais mettre des rappels systématiques dans mon agenda." },
        { register: 'Option 2', text: "Tu as raison de me le reprocher. La prochaine fois, je noterai tout immédiatement." },
        { register: 'Option 3', text: "C'est vrai que ce n'est pas professionnel. Je m'en excuse et je vais être plus vigilant." },
      ],
      pourquoi: "L'ironie et l'autocritique montrent une maturité linguistique. La réparation (je vais faire en sorte) est essentielle pour aller au-delà du simple constat.",
      aEviter: {
        text: '"Ce n\'est pas grave."',
        explanation: "Minimise le problème sans reconnaître sa responsabilité ni proposer de solution.",
      },
      pointDeLangue: {
        temps: 'Ironie + autocritique + réparation',
        examples: ["J'aurais dû...", 'Cela dit...', 'Je vais faire en sorte que...'],
        explanation: "Le conditionnel passé (j'aurais dû) pour l'autocritique + une promesse de réparation pour montrer la maturité.",
      },
    },
    {
      id: 'registre-formel',
      title: 'Adapter le registre (informel vers formel)',
      temps: 'Registre formel + politesse professionnelle',
      situation: {
        a: "Tu peux m'envoyer le document ?",
        b: 'Oui, pas de souci.',
      },
      comprehension: {
        question: 'Quel registre utilise B ?',
        options: [
          { text: 'Formel', correct: false },
          { text: 'Informel', correct: true },
          { text: 'Neutre', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases en registre formel.',
        options: [
          { text: "Bien sûr, je vous l'enverrai dans les plus brefs délais. Je vais vérifier une dernière fois les informations avant de vous le transmettre. N'hésitez pas à me faire savoir si vous avez besoin de modifications. Je reste à votre disposition pour toute question.", quality: 'best' },
          { text: "Oui, je t'envoie ça.", quality: 'ok' },
          { text: 'Prends-le.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes en registre formel',
      recordExample: "Bien sûr, je vous l'enverrai dans les plus brefs délais. Je reste à votre disposition.",
      modele: "Bien sûr, je vous l'enverrai dans les plus brefs délais. Je vais vérifier une dernière fois les informations avant de vous le transmettre. N'hésitez pas à me faire savoir si vous avez besoin de modifications. Je reste à votre disposition pour toute question.",
      variantes: [
        { register: 'Option 1', text: "Certainement. Je m'en occupe immédiatement et vous le transmettrai avant la fin de la journée." },
        { register: 'Option 2', text: "Avec plaisir. Permettez-moi de relire le document avant de vous l'envoyer." },
        { register: 'Option 3', text: "Tout à fait. Je vous l'adresse dès que possible. N'hésitez pas à me solliciter en cas de besoin." },
      ],
      pourquoi: "Savoir passer du registre informel au formel est une compétence B2 essentielle pour le monde professionnel.",
      aEviter: {
        text: '"Prends-le."',
        explanation: "Trop direct et familier — inapproprié dans un contexte professionnel.",
      },
      pointDeLangue: {
        temps: 'Registre formel + politesse professionnelle',
        examples: ['Dans les plus brefs délais', "N'hésitez pas à...", 'Je reste à votre disposition'],
        explanation: "Les formules de politesse professionnelle (vouvoiement, expressions formelles) montrent une maîtrise des registres.",
      },
    },
    {
      id: 'refuser-sans-non',
      title: 'Refuser sans dire "non"',
      temps: 'Refus diplomatique + alternative',
      situation: {
        a: 'Tu peux travailler ce week-end ?',
        b: 'Ça va être compliqué...',
      },
      comprehension: {
        question: 'Que veut dire B ?',
        options: [
          { text: 'Il accepte', correct: false },
          { text: 'Il refuse indirectement', correct: true },
          { text: 'Il demande', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases sans dire "non".',
        options: [
          { text: "Ça va être compliqué pour moi ce week-end, car j'ai déjà plusieurs engagements personnels. Je comprends que ce soit important, mais je ne pourrai malheureusement pas me rendre disponible. En revanche, je peux m'organiser pour avancer sur ce travail dès lundi. N'hésitez pas à me dire si cela vous convient.", quality: 'best' },
          { text: 'Non, je ne peux pas.', quality: 'ok' },
          { text: 'Je ne travaille pas.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes sans utiliser le mot "non"',
      recordExample: "Ça va être compliqué pour moi ce week-end. En revanche, je peux m'en occuper dès lundi.",
      modele: "Ça va être compliqué pour moi ce week-end, car j'ai déjà plusieurs engagements personnels. Je comprends que ce soit important, mais je ne pourrai malheureusement pas me rendre disponible. En revanche, je peux m'organiser pour avancer sur ce travail dès lundi. N'hésitez pas à me dire si cela vous convient.",
      variantes: [
        { register: 'Option 1', text: "Ce week-end s'annonce assez chargé pour moi. Cela dit, je pourrais consacrer du temps à ce projet en début de semaine prochaine." },
        { register: 'Option 2', text: "Malheureusement, je ne serai pas disponible ce week-end. Toutefois, je m'engage à avancer significativement dès lundi." },
        { register: 'Option 3', text: "J'aurais aimé pouvoir, mais ce week-end est déjà pris. Je vous propose de m'y mettre lundi à la première heure." },
      ],
      pourquoi: "Refuser sans dire 'non' est une compétence diplomatique essentielle. On utilise l'atténuation et une alternative constructive.",
      aEviter: {
        text: '"Je ne travaille pas."',
        explanation: "Trop catégorique et sans proposition d'alternative.",
      },
      pointDeLangue: {
        temps: 'Refus diplomatique + alternative',
        examples: ['Ça va être compliqué...', 'Malheureusement...', 'En revanche, je peux...'],
        explanation: "Utiliser des atténuateurs (compliqué, malheureusement) et toujours proposer une alternative.",
      },
    },
    {
      id: 'reformuler-clarifier',
      title: 'Reformuler et clarifier',
      temps: 'Reformulation + précision',
      situation: {
        a: 'Ce projet manque de clarté.',
        b: 'Que veux-tu dire ?',
      },
      comprehension: {
        question: 'Que demande B ?',
        options: [
          { text: 'Il refuse', correct: false },
          { text: 'Il demande une clarification', correct: true },
          { text: 'Il accepte', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec reformulation.',
        options: [
          { text: "Ce que je veux dire, c'est que certains objectifs ne sont pas clairement définis. Par exemple, les rôles de chacun ne sont pas précisés. Cela peut créer de la confusion dans l'équipe. Il serait donc utile de structurer davantage le projet.", quality: 'best' },
          { text: "Ce n'est pas clair.", quality: 'ok' },
          { text: "C'est mauvais.", quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes avec une reformulation claire',
      recordExample: "Ce que je veux dire, c'est que certains objectifs ne sont pas clairement définis. Il serait utile de structurer davantage le projet.",
      modele: "Ce que je veux dire, c'est que certains objectifs ne sont pas clairement définis. Par exemple, les rôles de chacun ne sont pas précisés. Cela peut créer de la confusion dans l'équipe. Il serait donc utile de structurer davantage le projet.",
      variantes: [
        { register: 'Option 1', text: "En d'autres termes, il manque un cadre précis. Les responsabilités ne sont pas suffisamment réparties." },
        { register: 'Option 2', text: "Pour être plus précis, je pense que les étapes du projet devraient être mieux définies et communiquées à tous." },
        { register: 'Option 3', text: "Autrement dit, le projet gagnerait à avoir une feuille de route plus détaillée avec des objectifs mesurables." },
      ],
      pourquoi: "Reformuler et préciser sa pensée est une compétence B2 essentielle pour éviter les malentendus dans un contexte professionnel.",
      aEviter: {
        text: '"C\'est mauvais."',
        explanation: "Jugement vague et improductif — ne précise ni le problème ni la solution.",
      },
      pointDeLangue: {
        temps: 'Reformulation + précision',
        examples: ['Ce que je veux dire...', "En d'autres termes...", 'Il serait donc utile de...'],
        explanation: "Les structures de reformulation permettent de clarifier sa pensée et de guider la conversation.",
      },
    },
    {
      id: 'nuancer-fortement',
      title: 'Nuancer fortement une opinion',
      temps: 'Nuance avancée + diplomatie',
      situation: {
        a: 'À mon avis, il faut toujours dire la vérité.',
        b: "Je ne suis pas entièrement d'accord.",
      },
      comprehension: {
        question: 'Que pense B ?',
        options: [
          { text: "Il est totalement d'accord", correct: false },
          { text: 'Il nuance fortement', correct: true },
          { text: 'Il refuse de parler', correct: false },
        ],
      },
      tuRepondsQuoi: {
        context: 'Réponds en 3-5 phrases avec nuance.',
        options: [
          { text: "Je comprends l'idée, et je suis d'accord dans la plupart des cas. Cependant, il existe des situations où dire toute la vérité peut blesser inutilement. Par exemple, dans certains contextes personnels, il vaut mieux faire preuve de tact. À mon avis, tout dépend de la situation et de la manière de s'exprimer.", quality: 'best' },
          { text: 'Non.', quality: 'ok' },
          { text: 'Tu as tort.', quality: 'bad' },
        ],
      },
      recordPrompt: 'Réponds en 30-60 secondes',
      recordExample: "Je comprends l'idée, mais il existe des situations où dire toute la vérité peut blesser. Tout dépend de la situation.",
      modele: "Je comprends l'idée, et je suis d'accord dans la plupart des cas. Cependant, il existe des situations où dire toute la vérité peut blesser inutilement. Par exemple, dans certains contextes personnels, il vaut mieux faire preuve de tact. À mon avis, tout dépend de la situation et de la manière de s'exprimer.",
      variantes: [
        { register: 'Option 1', text: "C'est un principe noble, néanmoins la réalité est plus complexe. Parfois, un mensonge par omission peut préserver une relation." },
        { register: 'Option 2', text: "En théorie, oui. Mais en pratique, la vérité brute peut être destructrice. Le tact est une forme d'intelligence sociale." },
        { register: 'Option 3', text: "Je suis partiellement d'accord. La vérité est importante, mais la façon de la dire l'est tout autant." },
      ],
      pourquoi: "Nuancer fortement une opinion montre une pensée complexe et une capacité à voir les différentes facettes d'un sujet.",
      aEviter: {
        text: '"Tu as tort."',
        explanation: "Trop frontal et sans argumentation — ne reflète pas le niveau B2.",
      },
      pointDeLangue: {
        temps: 'Nuance avancée + diplomatie',
        examples: ['Je comprends... cependant...', 'Il vaut mieux...', 'Tout dépend de...'],
        explanation: "Utiliser des concessions (je comprends), des oppositions (cependant) et des relativisations (tout dépend) pour nuancer.",
      },
    },
  ],
};

const LEVELS = ['A1', 'A2', 'B1', 'B2'];

const QUALITY_MAP = {
  best: { icon: '\u2705', label: 'Meilleure réponse', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  ok: { icon: '\ud83e\udd0f', label: 'Acceptable', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  bad: { icon: '\u274c', label: 'À éviter', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
};

function RecordButton({ testId }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch {
      alert('Microphone non disponible. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!isRecording ? (
        <button
          onClick={startRecording}
          className="flex items-center gap-1.5 text-xs text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg px-3 py-1.5 hover:bg-[#D4AF37]/5 transition-colors"
          data-testid={testId}>
          <Mic className="w-3 h-3" /> Enregistrer votre réponse
        </button>
      ) : (
        <button
          onClick={stopRecording}
          className="flex items-center gap-1.5 text-xs text-red-500 border border-red-300 rounded-lg px-3 py-1.5 bg-red-50 animate-pulse"
          data-testid={`${testId}-stop`}>
          <Square className="w-3 h-3" /> Arrêter
        </button>
      )}
      {audioUrl && (
        <audio controls src={audioUrl} className="h-8 max-w-[160px]" data-testid={`${testId}-playback`} />
      )}
    </div>
  );
}

function ActivityView({ activity, onBack }) {
  const [showModele, setShowModele] = useState(false);
  const [selectedComp, setSelectedComp] = useState(null);
  const [selectedReponse, setSelectedReponse] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const correctIndex = activity.comprehension.options.findIndex(o => o.correct);

  return (
    <div className="space-y-5" data-testid={`activity-${activity.id}`}>
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#D4AF37] transition-colors"
        data-testid="back-to-list">
        <ArrowRight className="w-3 h-3 rotate-180" /> Toutes les situations
      </button>

      {/* Title */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="activity-title">
          {activity.title}
        </h2>
        <p className="text-xs text-[#D4AF37] mt-1 font-semibold uppercase tracking-wider">{activity.temps}</p>
      </div>

      {/* Situation — dialogue */}
      <div className="bg-[#1A2F4A] rounded-xl p-5 space-y-3" data-testid="situation-section">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Situation</p>
        </div>
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <span className="text-xs font-bold text-[#D4AF37] bg-[#D4AF37]/10 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">A</span>
            <p className="text-sm text-white/90 leading-relaxed">{activity.situation.a}</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-xs font-bold text-white bg-white/10 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">B</span>
            <p className="text-sm text-white/90 leading-relaxed">{activity.situation.b}</p>
          </div>
        </div>
      </div>

      {/* Compréhension */}
      <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="comprehension-section">
        <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Compréhension</p>
        <p className="text-sm font-semibold text-[#1A2F4A]">{activity.comprehension.question}</p>
        <div className="space-y-2">
          {activity.comprehension.options.map((opt, i) => {
            const isSelected = selectedComp === i;
            const isCorrect = opt.correct;
            let optClass = 'bg-[#FAF7F0] border border-[#E8E4DC] hover:border-[#D4AF37]';
            if (isSelected && isCorrect) optClass = 'bg-emerald-50 border-2 border-emerald-400';
            else if (isSelected && !isCorrect) optClass = 'bg-red-50 border-2 border-red-300';
            else if (selectedComp !== null && isCorrect) optClass = 'bg-emerald-50 border border-emerald-300';

            return (
              <button
                key={i}
                onClick={() => setSelectedComp(i)}
                className={`w-full text-left rounded-lg px-4 py-2.5 text-sm transition-all ${optClass}`}
                data-testid={`comp-option-${i}`}>
                <span className="font-bold text-[#D4AF37] mr-2">{String.fromCharCode(97 + i)})</span>
                <span className="text-[#1A2F4A]">{opt.text}</span>
                {isSelected && isCorrect && <span className="ml-2">{'\u2705'}</span>}
                {isSelected && !isCorrect && <span className="ml-2">{'\u274c'}</span>}
                {selectedComp !== null && !isSelected && isCorrect && <span className="ml-2">{'\u2705'}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tu réponds quoi? */}
      <div className="bg-white border-2 border-[#D4AF37]/20 rounded-xl p-5 space-y-3" data-testid="tu-reponds-section">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Tu réponds quoi ?</p>
        </div>
        <p className="text-sm text-[#6B4E37] italic">{activity.tuRepondsQuoi.context}</p>
        <p className="text-xs text-[#999] font-semibold uppercase mt-1">Choisis la meilleure réponse :</p>
        <div className="space-y-2">
          {activity.tuRepondsQuoi.options.map((opt, i) => {
            const q = QUALITY_MAP[opt.quality];
            const isSelected = selectedReponse === i;
            return (
              <button
                key={i}
                onClick={() => setSelectedReponse(i)}
                className={`w-full text-left rounded-lg px-4 py-2.5 text-sm transition-all border ${
                  isSelected ? `${q.bg} ${q.border} border-2` : 'bg-[#FAF7F0] border-[#E8E4DC] hover:border-[#D4AF37]'
                }`}
                data-testid={`reponse-option-${i}`}>
                <span className="font-bold text-[#D4AF37] mr-2">{String.fromCharCode(97 + i)})</span>
                <span className="text-[#1A2F4A]">{opt.text}</span>
                {isSelected && <span className="ml-2">{q.icon}</span>}
              </button>
            );
          })}
        </div>
        {selectedReponse !== null && (
          <p className={`text-xs font-semibold mt-1 ${QUALITY_MAP[activity.tuRepondsQuoi.options[selectedReponse].quality].color}`}>
            {QUALITY_MAP[activity.tuRepondsQuoi.options[selectedReponse].quality].label}
          </p>
        )}
      </div>

      {/* Record */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#D4AF37]/30" />
          <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider whitespace-nowrap">Enregistre ta réponse</p>
          <div className="h-px flex-1 bg-[#D4AF37]/30" />
        </div>
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="record-section">
          <p className="text-sm text-[#1A2F4A]">{activity.recordPrompt}</p>
          <p className="text-xs text-[#999] italic">Ex : "{activity.recordExample}"</p>
          <RecordButton testId="record-reponse" />
          <div className="bg-[#1A2F4A]/5 rounded-lg px-3 py-2 mt-2">
            <p className="text-[10px] text-[#6B4E37] leading-relaxed">
              Ton audio est sauvegardé dans "Mes enregistrements". Tu peux demander un retour personnalisé depuis ton dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Modèle — hidden */}
      <button
        onClick={() => setShowModele(!showModele)}
        className="w-full flex items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-bold hover:border-[#D4AF37] transition-colors"
        data-testid="modele-toggle">
        <Eye className="w-4 h-4" /> {showModele ? 'Cacher le modèle' : 'Voir un exemple'}
      </button>
      {showModele && (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 space-y-4" data-testid="modele-content">
          <div>
            <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-2">Modèle de réponse</p>
            <p className="text-sm text-[#1A2F4A] italic leading-relaxed">{activity.modele}</p>
          </div>

          {/* Variantes */}
          <div>
            <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold mb-2">Variantes possibles</p>
            <div className="space-y-1.5">
              {activity.variantes.map((v, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs text-[#D4AF37] font-bold min-w-[70px]">{v.register} :</span>
                  <span className="text-sm text-[#1A2F4A]">{v.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pourquoi + À éviter + Point de langue — collapsible */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full flex items-center justify-between bg-[#1A2F4A] rounded-xl px-5 py-3 text-sm font-semibold text-white hover:bg-[#243a58] transition-colors"
        data-testid="details-toggle">
        <span>Explications & point de langue</span>
        {showDetails ? <ChevronUp className="w-4 h-4 text-[#D4AF37]" /> : <ChevronDown className="w-4 h-4 text-[#D4AF37]" />}
      </button>
      {showDetails && (
        <div className="space-y-4" data-testid="details-content">
          {/* Pourquoi */}
          <div className="bg-white border border-[#E8E4DC] rounded-xl p-5" data-testid="pourquoi-section">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-3.5 h-3.5 text-[#D4AF37]" />
              <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Pourquoi ?</p>
            </div>
            <p className="text-sm text-[#1A2F4A] leading-relaxed">{activity.pourquoi}</p>
          </div>

          {/* À éviter */}
          <div className="bg-red-50/50 border border-red-200/50 rounded-xl p-5" data-testid="a-eviter-section">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              <p className="text-xs text-red-500 uppercase tracking-wider font-bold">À éviter</p>
            </div>
            <p className="text-sm text-red-600 font-semibold">{activity.aEviter.text}</p>
            <p className="text-xs text-red-400 mt-1">{activity.aEviter.explanation}</p>
          </div>

          {/* Point de langue */}
          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5" data-testid="point-langue-section">
            <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold mb-2">
              Point de langue — {activity.pointDeLangue.temps}
            </p>
            <div className="space-y-1 mb-2">
              {activity.pointDeLangue.examples.map((ex, i) => (
                <p key={i} className="text-sm text-[#1A2F4A] italic">
                  <span className="text-[#D4AF37] mr-1">&bull;</span> {ex}
                </p>
              ))}
            </div>
            <p className="text-xs text-[#6B4E37]">{activity.pointDeLangue.explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReagisVite() {
  const [activeLevel, setActiveLevel] = useState('A1');
  const [activeActivity, setActiveActivity] = useState(null);

  const activities = ACTIVITIES[activeLevel] || [];

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Hero */}
      <section className="pt-16 pb-8">
        <div className="max-w-[520px] mx-auto px-8 text-center">
          <Link to="/speaking-lab" className="inline-flex items-center gap-1 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-to-lab">
            <ArrowRight className="w-3 h-3 rotate-180" /> Speaking Lab
          </Link>
          <h1
            className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="page-title">
            Réagis vite !
          </h1>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#D4AF37]/50" />
            <Zap className="w-4 h-4 text-[#D4AF37]/60" />
            <div className="h-px w-8 bg-[#D4AF37]/50" />
          </div>
          <p className="text-xs text-[#6B4E37] italic">
            Réponds naturellement, pense en français.
          </p>
        </div>
      </section>

      {/* Level Tabs */}
      <section className="pb-4">
        <div className="max-w-[520px] mx-auto px-8 flex gap-2 justify-center" data-testid="level-tabs">
          {LEVELS.map(lvl => {
            const hasContent = (ACTIVITIES[lvl] || []).length > 0;
            return (
              <button
                key={lvl}
                onClick={() => { if (hasContent) { setActiveLevel(lvl); setActiveActivity(null); } }}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  activeLevel === lvl
                    ? 'bg-[#1A2F4A] text-white'
                    : hasContent
                      ? 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                      : 'bg-[#F3F4F6] text-[#ccc] cursor-not-allowed'
                }`}
                data-testid={`level-btn-${lvl}`}>
                {lvl} {!hasContent && <span className="text-[9px] ml-0.5 opacity-60">soon</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-[520px] mx-auto px-8">
          {activeActivity ? (
            <ActivityView activity={activeActivity} onBack={() => setActiveActivity(null)} />
          ) : (
            <div className="space-y-3" data-testid="activity-list">
              {activities.length === 0 ? (
                <div className="text-center py-16">
                  <Zap className="w-8 h-8 text-[#E8E4DC] mx-auto mb-3" />
                  <p className="text-sm text-[#999]">Contenu {activeLevel} bientôt disponible</p>
                </div>
              ) : (
                activities.map((act, i) => (
                  <button
                    key={act.id}
                    onClick={() => setActiveActivity(act)}
                    className="w-full bg-white border border-[#E8E4DC] rounded-xl overflow-hidden hover:border-[#D4AF37] hover:shadow-sm transition-all text-left group"
                    data-testid={`activity-btn-${act.id}`}>
                    <div className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1A2F4A] text-white flex items-center justify-center flex-shrink-0">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {act.title}
                        </p>
                        <p className="text-xs text-[#999] mt-0.5">{act.temps} &middot; ~1 min</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#E8E4DC] group-hover:text-[#D4AF37] transition-colors" />
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
